/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import SidenavCollapse from "templates/Sidenav/SidenavCollapse";
import LogoutButton from "templates/Sidenav/LogoutButton";

// Custom styles for the Sidenav
import SidenavRoot from "templates/Sidenav/SidenavRoot";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

function Sidenav({ color, brand, brandName, routes, onLogout, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const location = useLocation();
  const theme = useTheme();
  // Below the "xl" breakpoint the sidenav behaves like an off-canvas drawer
  const isMobile = useMediaQuery(theme.breakpoints.down("xl"));
  const collapseName = location.pathname.split("/")[1];
  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);
  // Only close after a navigation on small screens (on desktop the sidenav stays as is)
  const closeSidenavOnMobile = () => {
    if (isMobile) closeSidenav();
  };

  // Set the initial state and update it ONLY when crossing the mobile/desktop breakpoint.
  // (The old resize listener fired on every resize, e.g. when the mobile keyboard opened or
  // the URL bar collapsed, which made the drawer close unexpectedly.)
  useEffect(() => {
    setMiniSidenav(dispatch, isMobile);
    if (isMobile) {
      setTransparentSidenav(dispatch, false);
      setWhiteSidenav(dispatch, false);
    }
  }, [dispatch, isMobile]);

  // Always close the drawer after navigating on mobile
  useEffect(() => {
    if (isMobile) closeSidenav();
  }, [location.pathname]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(({ type, name, icon, title, noCollapse, key, href, route }) => {
    let returnValue;

    if (type === "collapse") {
      returnValue = href ? (
        <Link
          href={href}
          key={key}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavCollapse
            name={name}
            icon={icon}
            active={key === collapseName}
            noCollapse={noCollapse}
          />
        </Link>
      ) : (
        <NavLink
          key={key}
          to={route}
          onClick={closeSidenavOnMobile}
          aria-current={key === collapseName ? "page" : undefined}
          style={{ textDecoration: "none" }}
        >
          <SidenavCollapse
            name={name}
            icon={icon}
            active={key === collapseName}
            noCollapse={noCollapse}
          />
        </NavLink>
      );
    } else if (type === "title") {
      returnValue = (
        <MDTypography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </MDTypography>
      );
    } else if (type === "divider") {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }

    return returnValue;
  });

  const dividerLight =
    (!darkMode && !whiteSidenav && !transparentSidenav) ||
    (darkMode && !transparentSidenav && whiteSidenav);

  // Hover-to-expand only makes sense with a mouse; on touch devices the emulated
  // mouseenter events caused the sidenav to flicker/toggle.
  const hoverProps = isMobile ? {} : rest;

  return (
    <SidenavRoot
      {...hoverProps}
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? !miniSidenav : true}
      onClose={closeSidenav}
      ModalProps={{ keepMounted: true }}
      PaperProps={{ component: "nav", "aria-label": "Main navigation" }}
      ownerState={{
        transparentSidenav,
        whiteSidenav,
        // On mobile open/close is handled by the Drawer itself, so always use the "open" styles
        miniSidenav: isMobile ? false : miniSidenav,
        darkMode,
      }}
    >
      <MDBox display="flex" flexDirection="column" height="100%" sx={{ overflow: "hidden" }}>
        <MDBox pt={3} pb={1} px={4} textAlign="center" position="relative">
          {isMobile && (
            <IconButton
              aria-label="Close navigation menu"
              onClick={closeSidenav}
              sx={{ position: "absolute", top: 8, right: 8, color: "#C89B3C" }}
            >
              <Icon>close</Icon>
            </IconButton>
          )}

          <MDBox
            component={NavLink}
            to="/"
            display="flex"
            alignItems="center"
            onClick={closeSidenavOnMobile}
          >
            <MDBox className="d-flex justify-content-center align-items-center rounded">
              {brand ? (
                <MDBox
                  component="img"
                  src={brand}
                  alt="Brand"
                  bgColor={color}
                  width="60%"
                  variant="gradient"
                  borderRadius="lg"
                  shadow="xl"
                />
              ) : (
                ""
              )}
            </MDBox>
          </MDBox>
          <div className="d-flex justify-content-center align-items-center rounded pt-3">
            <MDBox color={textColor}>{brandName}</MDBox>
          </div>
        </MDBox>
        <Divider light={dividerLight} />

        {/* Scrollable on short screens (landscape phones) so no item is ever unreachable */}
        <List sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>{renderRoutes}</List>

        {onLogout && (
          <MDBox pb={{ xs: "max(16px, env(safe-area-inset-bottom))", xl: 2 }}>
            <Divider light={dividerLight} />
            <LogoutButton onClick={onLogout} />
          </MDBox>
        )}
      </MDBox>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  onLogout: PropTypes.func,
};

export default Sidenav;
